'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { AccessoryCrystallizeData } from '@/ui/components/pages/ProductDetail/AccessoryDetail/types/AccessoryCrystallizeData';
import { SmartlockCrystallizeData } from '@/ui/components/pages/ProductDetail/SmartlockDetail/types/SmartlockCrystallizeData';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useClubOnlyPurchase } from '@/ui/components/club-only-purchase-actions/use-club-only-purchase';
import { ClubOnlyPurchaseActions } from '@/ui/components/club-only-purchase-actions/club-only-purchase-actions';
import { CLUB_PDP_NAG_VALUE } from '@/utils/constants';
import { useClassicPdpProduct } from '@/ui/providers/classic-pdp-product-provider';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { Accessory } from '@/ui/services/AccessoryService';
import { Smartlock } from '@/ui/services/SmartlockService';
import { useCartStore } from '@/ui/store/cart';
import type { ICartState } from '@/ui/store/cart/types';
import { DocShapeId } from '@/utils/shapeIds';
import { ClassicBundleDetailSection } from './bundle-detail/classic-bundle-detail-section';
import {
  ClassicPdpProductData,
  ClassicProductDetailTile,
  getClassicPdpProductRef,
  mapClassicPdpBreadcrumbs,
  mapClassicPdpContent,
  mapClassicPdpUpsell,
} from './classic-pdp-content';
import { isClassicPdpBundleData, isClassicPdpProductData } from './classic-pdp-entry';
import { ClassicPdpLayout } from './classic-pdp-layout';
import { ProductImageGallery } from './product-image-gallery/product-image-gallery';
import { PurchasePanel } from './purchase-panel/purchase-panel';
import { SmartHomeCompatibility } from './smart-home-compatibility/smart-home-compatibility';
import { ClassicPdpProduct, useClassicPdpSelection } from './use-classic-pdp-selection';

// The CMS component contract lives with the mapper that reads it; the tile
// re-exports it so the tile renderer needs one import only.
export type { ClassicPdpProductData, ClassicProductDetailTile } from './classic-pdp-content';

export interface ClassicProductDetailProps {
  tile: ClassicProductDetailTile;
}

function createHardwareProduct(
  data: ClassicPdpProductData,
  cartStore: ICartState,
): ClassicPdpProduct {
  return data.shape.identifier === DocShapeId.smartLock
    ? new Smartlock(data as SmartlockCrystallizeData, cartStore)
    : new Accessory(data as AccessoryCrystallizeData, cartStore);
}

interface ClassicProductDetailSectionProps {
  data: ClassicPdpProductData;
  tile: ClassicProductDetailTile;
}

/**
 * Classic two-column product detail section: a breadcrumb trail on top,
 * gallery on the left, purchase panel on the right (sticky from `lg` up), and
 * the upsell offers of the tile across the full width underneath. Below `lg`
 * the DOM order alone produces the required single column (images, purchase
 * options, offers) and nothing sticks.
 */
function ClassicProductDetailSection({ data, tile }: Readonly<ClassicProductDetailSectionProps>) {
  const t = useTranslations('ClassicProductDetail');
  const cartStore = useCartStore();
  const { country, areaVariables } = useLocalization();
  const { formatCurrency } = useCurrencyFormatter();
  const productRuleService = useProductRuleService();

  const product = useMemo(() => createHardwareProduct(data, cartStore), [data, cartStore]);
  const content = useMemo(() => mapClassicPdpContent(data, tile, product), [data, tile, product]);
  const upsell = useMemo(
    () => mapClassicPdpUpsell(tile, areaVariables?.priceVariantId),
    [tile, areaVariables],
  );
  const homeLabel = t('home');
  const breadcrumbs = useMemo(
    () => mapClassicPdpBreadcrumbs(tile, data, content.title, homeLabel),
    [tile, data, content.title, homeLabel],
  );
  const selection = useClassicPdpSelection(product);
  const { currentVariant } = selection;
  const variantReference = currentVariant && product.getVariantReference(currentVariant);
  const clubOnlyPurchase = useClubOnlyPurchase(
    !!variantReference && productRuleService.isVariantRestrictedToNukiClub(variantReference),
  );

  if (!currentVariant || !variantReference) {
    return null;
  }

  const price = currentVariant.priceVariant ? formatCurrency(currentVariant.priceVariant) : '';

  const visuals = (
    <>
      <ProductImageGallery
        images={currentVariant.images}
        title={content.title}
        colorName={currentVariant.color?.name}
        resetKey={currentVariant.sku}
        badges={productRuleService.getVariantBadges(variantReference)}
      />
      <SmartHomeCompatibility icons={content.compatibilityIcons} />
    </>
  );

  const actions = (
    <PurchasePanel
      title={content.title}
      subtitle={content.subtitle}
      shortDescription={content.shortDescription}
      usps={content.usps}
      price={price}
      isVatNoteHidden={country.toUpperCase() === 'US'}
      isRatingVisible={content.isRatingVisible}
      colorHeading={product.colorHeading}
      cylinderHeading={product.cylinderHeading}
      wildcardHeading={'wildcardHeading' in product ? product.wildcardHeading : undefined}
      selection={selection}
      isAddToCartDisabled={clubOnlyPurchase.gate === 'waiting'}
      purchaseActions={
        clubOnlyPurchase.gate === 'club-actions' ? (
          <ClubOnlyPurchaseActions
            isLoggedIn={clubOnlyPurchase.isLoggedIn}
            nag={CLUB_PDP_NAG_VALUE}
            onLogIn={clubOnlyPurchase.logInToUnlock}
          />
        ) : undefined
      }
    />
  );

  return (
    <ClassicPdpLayout
      breadcrumbs={breadcrumbs}
      visuals={visuals}
      actions={actions}
      upsell={upsell}
    />
  );
}

/**
 * Editor-placed tile that renders the classic product detail section for the
 * product or bundle it links. Texts, icons and upsell offers come from the tile
 * document, everything else from the linked product, so the tile renders the
 * same on a product page and on a standalone page.
 */
export function ClassicProductDetail({ tile }: Readonly<ClassicProductDetailProps>) {
  const productRef = getClassicPdpProductRef(tile);
  const data = useClassicPdpProduct(productRef?.path) ?? null;

  if (isClassicPdpBundleData(data)) {
    return <ClassicBundleDetailSection data={data} tile={tile} />;
  }

  if (!isClassicPdpProductData(data)) {
    return null;
  }

  return <ClassicProductDetailSection data={data} tile={tile} />;
}
